<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { refDebounced } from '@vueuse/core';
import { UiText, UiInputField, UiIcon } from '@shopexperts/shared';
import type { AdminExpertClaim } from '@shopexperts/legacy-api';
import { useAdminExpertClaims, useAdminExpertClaimDetail } from '@/app/composables/expert-claims/useAdminExpertClaims';
import { useExpertClaimActions } from '@/app/composables/expert-claims/useExpertClaimActions';
import {
  claimDecisionFilterOptions,
  claimStatusFilterOptions,
  claimTypeFilterOptions,
} from '@/app/components/expert-claims/claimAppearance';
import ExpertClaimCard from '@/app/components/expert-claims/ExpertClaimCard.vue';
import ExpertClaimFilters from '@/app/components/expert-claims/ExpertClaimFilters.vue';
import ExpertClaimDetailDrawer from '@/app/components/expert-claims/ExpertClaimDetailDrawer.vue';
import ApproveClaimDialog from '@/app/components/expert-claims/ApproveClaimDialog.vue';
import RejectClaimDialog from '@/app/components/expert-claims/RejectClaimDialog.vue';
import CancelDecisionDialog from '@/app/components/expert-claims/CancelDecisionDialog.vue';
import EditClaimDialog from '@/app/components/expert-claims/EditClaimDialog.vue';
import EmptyDataPlaceholder from '@/components/common/EmptyDataPlaceholder.vue';
import LoadingCard from '@/components/common/LoadingCard.vue';

const route = useRoute();
const router = useRouter();

// Hydrate state from the URL query so refreshing / sharing a link restores
// the search and filters. Unknown values are dropped rather than sent to the
// API (which 422s on bad filter values).
function readQuery(key: string, allowed?: string[]): string {
  const value = route.query[key];
  if (typeof value !== 'string') return '';
  if (allowed && !allowed.includes(value)) return '';
  return value;
}

const searchQuery = ref(readQuery('search'));
const decisionFilter = ref(readQuery('decision', ['none', 'approved', 'rejected', 'any']));
const statusFilter = ref(readQuery('status', ['pending', 'verified']));
const typeFilter = ref(readQuery('type', ['freelancer', 'agency']));

// Persist filters/search in the URL using router.replace so browser history
// stays clean (no new entry per keystroke). Debounced so rapid typing in the
// search box doesn't thrash the router.
let syncTimer: ReturnType<typeof setTimeout> | null = null;

async function syncQueryToUrl() {
  const next: Record<string, string> = {};
  if (searchQuery.value) next.search = searchQuery.value;
  if (decisionFilter.value) next.decision = decisionFilter.value;
  if (statusFilter.value) next.status = statusFilter.value;
  if (typeFilter.value) next.type = typeFilter.value;

  // Avoid no-op replaces (which can still trigger router internals).
  const current = route.query;
  const sameKeys =
    Object.keys(next).length === Object.keys(current).length &&
    Object.entries(next).every(([key, value]) => current[key] === value);
  if (sameKeys) return;

  await router.replace({ query: next });
}

watch([searchQuery, decisionFilter, statusFilter, typeFilter], () => {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(syncQueryToUrl, 200);
});

onBeforeUnmount(() => {
  if (syncTimer) clearTimeout(syncTimer);
});

// The query key debounce is separate from the URL-sync debounce above: typing
// updates the input immediately, but the list request only fires once typing
// pauses — one request instead of one per keystroke.
const debouncedSearch = refDebounced(searchQuery, 250);

const {
  claims,
  isLoading,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useAdminExpertClaims({
  search: debouncedSearch,
  decision: decisionFilter,
  status: statusFilter,
  type: typeFilter,
});

const hasActiveCriteria = computed(
  () => !!(searchQuery.value || decisionFilter.value || statusFilter.value || typeFilter.value),
);

function resetFilters() {
  searchQuery.value = '';
  decisionFilter.value = '';
  statusFilter.value = '';
  typeFilter.value = '';
}

// Active-filter tags are derived from the page's own v-model state (the same
// refs the filter panel binds to), so there is no template-ref indirection.
interface ActiveFilterTag {
  key: 'decision' | 'status' | 'type';
  label: string;
  color?: string;
}

const activeFilterTags = computed<ActiveFilterTag[]>(() => {
  const tags: ActiveFilterTag[] = [];
  if (decisionFilter.value) {
    const option = claimDecisionFilterOptions.find((o) => o.value === decisionFilter.value);
    tags.push({ key: 'decision', label: option?.label ?? decisionFilter.value, color: option?.dot });
  }
  if (statusFilter.value) {
    const option = claimStatusFilterOptions.find((o) => o.value === statusFilter.value);
    tags.push({ key: 'status', label: `Email ${option?.label.toLowerCase() ?? statusFilter.value}`, color: option?.dot });
  }
  if (typeFilter.value) {
    const option = claimTypeFilterOptions.find((o) => o.value === typeFilter.value);
    tags.push({ key: 'type', label: option?.label ?? typeFilter.value });
  }
  return tags;
});

function removeFilter(key: ActiveFilterTag['key']) {
  const modelMap = { decision: decisionFilter, status: statusFilter, type: typeFilter };
  modelMap[key].value = '';
}

// Infinite scroll observer. Held in a ref so unmount can disconnect it —
// the sentinel watcher is stopped before template refs are nulled, so it
// never fires with `el = null` during teardown.
const sentinel = ref<HTMLElement | null>(null);
const observer = ref<IntersectionObserver | null>(null);

watch(sentinel, (el) => {
  observer.value?.disconnect();
  if (!el) return;
  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { rootMargin: '200px' },
  );
  observer.value.observe(el);
});

onBeforeUnmount(() => {
  observer.value?.disconnect();
  observer.value = null;
});

// --- Detail drawer ---
// The drawer shows a dedicated detail query (kept fresh by mutations'
// invalidation) and falls back to the clicked list row while it loads —
// a decided claim that drops out of a filtered list stays visible.
const selectedClaimId = ref<number | null>(null);
const selectedClaimSnapshot = ref<AdminExpertClaim | null>(null);
const drawerOpen = ref(false);

const { claim: detailClaim } = useAdminExpertClaimDetail(selectedClaimId);

const activeClaim = computed<AdminExpertClaim | null>(() => {
  if (detailClaim.value && detailClaim.value.id === selectedClaimId.value) {
    return detailClaim.value;
  }
  return selectedClaimSnapshot.value;
});

function openClaim(claim: AdminExpertClaim) {
  selectedClaimSnapshot.value = claim;
  selectedClaimId.value = claim.id;
  drawerOpen.value = true;
}

// --- Decision dialogs ---
const approveDialogOpen = ref(false);
const rejectDialogOpen = ref(false);
const cancelDialogOpen = ref(false);
const editDialogOpen = ref(false);
// Unsaved note draft handed over by the drawer, prefilled into the dialog.
const decisionNotePrefill = ref<string | undefined>();

function openApproveDialog(draftNote?: string) {
  decisionNotePrefill.value = draftNote;
  approveDialogOpen.value = true;
}

function openRejectDialog(draftNote?: string) {
  decisionNotePrefill.value = draftNote;
  rejectDialogOpen.value = true;
}

const { approve, reject, cancelDecision } = useExpertClaimActions();

async function confirmApprove(note?: string) {
  if (!activeClaim.value) return;
  try {
    await approve.mutateAsync({ claimId: activeClaim.value.id, adminNote: note });
  } catch {
    // Toasted (server message verbatim) + list refetched by the composable.
  } finally {
    approveDialogOpen.value = false;
  }
}

async function confirmReject(note?: string) {
  if (!activeClaim.value) return;
  try {
    await reject.mutateAsync({ claimId: activeClaim.value.id, adminNote: note });
  } catch {
    // Toasted + refetched by the composable.
  } finally {
    rejectDialogOpen.value = false;
  }
}

async function confirmCancelDecision() {
  if (!activeClaim.value) return;
  try {
    await cancelDecision.mutateAsync({ claimId: activeClaim.value.id });
  } catch {
    // Toasted + refetched by the composable.
  } finally {
    cancelDialogOpen.value = false;
  }
}
</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
    <!-- Header -->
    <div class="mb-6">
      <UiText as="h1" variant="h3" text="**Expert Claims**" />
      <UiText
        as="p"
        variant="p-small"
        class="text-gray-500 mt-1"
        text="Review claim requests and decide who owns each expert profile"
      />
    </div>

    <!-- Search + Filters -->
    <div class="mb-6 space-y-2.5">
      <div class="flex items-center gap-3">
        <UiInputField
          v-model="searchQuery"
          placeholder="Search by name, email, company, or profile slug..."
          icon="search"
          size="md"
          class="max-w-sm flex-1"
        />

        <ExpertClaimFilters
          v-model:decision="decisionFilter"
          v-model:status="statusFilter"
          v-model:type="typeFilter"
          @reset="resetFilters"
        />
      </div>

      <!-- Active filter tags -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <div
          v-if="activeFilterTags.length > 0"
          class="flex items-center gap-2 flex-wrap"
        >
          <button
            v-for="filter in activeFilterTags"
            :key="filter.key"
            @click="removeFilter(filter.key)"
            class="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium
                   bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800
                   border border-gray-100 hover:border-gray-200
                   transition-all duration-150 cursor-pointer"
          >
            <span
              v-if="filter.color"
              class="w-1.5 h-1.5 rounded-full shrink-0"
              :class="filter.color"
            />
            {{ filter.label }}
            <UiIcon name="close" class="w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity" />
          </button>

          <button
            @click="resetFilters"
            class="text-[11px] text-gray-400 hover:text-gray-600 transition-colors ml-1"
          >
            Clear all
          </button>
        </div>
      </Transition>
    </div>

    <!-- Loading -->
    <LoadingCard v-if="isLoading" />

    <!-- Claims list -->
    <div v-else-if="claims.length > 0" class="space-y-3">
      <ExpertClaimCard
        v-for="(claim, index) in claims"
        :key="claim.id"
        :claim="claim"
        :style="{ animationDelay: `${Math.min(index, 10) * 40}ms` }"
        class="animate-fade-in-up"
        @click="openClaim(claim)"
      />

      <!-- Infinite scroll sentinel -->
      <div ref="sentinel" class="h-1" />

      <div v-if="isFetchingNextPage" class="py-4 text-center">
        <UiText as="p" variant="p-small" class="text-gray-400" text="Loading more..." />
      </div>
    </div>

    <!-- Empty states -->
    <EmptyDataPlaceholder
      v-else-if="hasActiveCriteria"
      title="No claims found"
      description="Try adjusting your search or filters"
    />
    <EmptyDataPlaceholder
      v-else
      title="No claims yet"
      description="Claim requests from expert profiles will appear here"
    />

    <!-- Detail drawer -->
    <ExpertClaimDetailDrawer
      v-model="drawerOpen"
      :claim="activeClaim"
      @approve="openApproveDialog"
      @reject="openRejectDialog"
      @cancel-decision="cancelDialogOpen = true"
      @edit="editDialogOpen = true"
    />

    <!-- Decision dialogs -->
    <ApproveClaimDialog
      v-model="approveDialogOpen"
      :claim="activeClaim"
      :initial-note="decisionNotePrefill"
      :is-loading="approve.isPending.value"
      @confirm="confirmApprove"
    />
    <RejectClaimDialog
      v-model="rejectDialogOpen"
      :claim="activeClaim"
      :initial-note="decisionNotePrefill"
      :is-loading="reject.isPending.value"
      @confirm="confirmReject"
    />
    <CancelDecisionDialog
      v-model="cancelDialogOpen"
      :claim="activeClaim"
      :is-loading="cancelDecision.isPending.value"
      @confirm="confirmCancelDecision"
    />
    <EditClaimDialog
      v-model="editDialogOpen"
      :claim="activeClaim"
    />
  </main>
</template>

<style scoped>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out both;
}
</style>
